import { FC, useEffect, useMemo } from 'react';

import { getDateFromString, getMaxDate, TODAY_START_OF_DAY } from 'utils/date';

import { useBreakpoint } from '@hh.ru/magritte-ui';

import { useMultiCreationDateValidation } from 'hooks/multiCreation/useMultiCreationDateValidation';
import useTrls from 'hooks/translation/useTrls';

import { DateSelect } from 'components/common/DateSelect/DateSelect';

import { AuctionBuilderDatesErrorCode } from 'store/models/auctionData';
import { selectMultiCreationCampaignsCreating, setMultiCreationEndAt } from 'store/models/multiCreation';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    datePlaceholder: 'MultiCreationAuction.BidsAndBudgetsTable.dates.placeholder',
    endDatePlaceholder: 'MultiCreationAuction.BidsAndBudgetsTable.dates.endDatePlaceholder',
    error: {
        [AuctionBuilderDatesErrorCode.ValueMissingForStrategy]:
            'AuctionBuilder.CampaignPeriod.error.valueMissingForStrategy',
    },
};

export const AuctionDateEnd: FC<{ vacancyId: number }> = ({ vacancyId }) => {
    const currentAuctionsToCreate = useSelector(({ multiCreation }) => multiCreation.auctionsToCreate[vacancyId]);
    const { startAt, endAt, endAtValidationError } = currentAuctionsToCreate;

    const isCampaignsCreating = useSelector(selectMultiCreationCampaignsCreating);
    const { isMobile } = useBreakpoint();
    const dispatch = useDispatch();
    const trls = useTrls();
    const { validateEndDate } = useMultiCreationDateValidation(vacancyId);

    const selectedDate = useMemo(() => getDateFromString(endAt), [endAt]);

    const minDate = useMemo(() => {
        const dates = [getDateFromString(startAt), TODAY_START_OF_DAY].filter((date) => !!date) as Date[];
        return getMaxDate(dates);
    }, [startAt]);

    useEffect(() => {
        validateEndDate(endAt);
    }, [endAt, validateEndDate]);

    const handleDateSelect = (date: string) => {
        dispatch(setMultiCreationEndAt({ vacancyId, value: date }));

        validateEndDate(date);
    };

    return (
        <DateSelect
            data-qa="auction-date-end-input"
            minDate={minDate}
            onDateSelect={handleDateSelect}
            placeholder={trls(TrlKeys[isMobile ? 'endDatePlaceholder' : 'datePlaceholder'])}
            selectedDate={selectedDate || undefined}
            inputDisabled={isCampaignsCreating}
            inputInvalid={!!endAtValidationError}
            errorMessage={endAtValidationError || undefined}
            elevatePlaceholder={isMobile}
            inputSize={isMobile ? 'large' : 'medium'}
        />
    );
};
